import React from 'react';
import { findDOMNode } from 'react-dom';
import { Modal, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { fetchFlowLibraryItems } from '../../actions/reduxActions/flowLibrary';
import { connect } from 'react-redux';
import { pathOr } from 'ramda';
import PlayerSource from '../../sources/player';
import { addNotification } from '../../actions/reduxActions/notification';
import { RUNTIME_URI } from '../../constants';

class Run extends React.Component {

    constructor(props) {
        super(props);
        this.onReportingModeChange = this.onReportingModeChange.bind(this);
        this.onCopyUrl = this.onCopyUrl.bind(this);
        this.runFlow = this.runFlow.bind(this);
        this.state = {
            isBuilding: true,
            players: [],
            reportingMode: '',
            hasUserSelectedPlayer: false,
            flowLibraryPlayer: null,
            isCheckingFlowLibrary: true,
        };

        this.runtimeUri = `${RUNTIME_URI}/`;
    }

    componentDidMount() {
        this.build();
        this.props.fetchFlowLibraryItems();
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.items !== prevProps.items ||
            this.props.flowId !== prevProps.flowId ||
            this.props.tenantId !== prevProps.tenantId ||
            this.props.selectedPlayer !== prevProps.selectedPlayer
        ) {
            const isInFlowLibrary = this.props.items && this.props.items.find(flow => flow.flowId === this.props.flowId);
            const playerName = pathOr(null, ['preferredPlayer', 'name'], isInFlowLibrary);

            // This flow does have a preferredPlayer in Flow Library
            if (playerName) {
                // Check that this Player exists in their Tenant
                PlayerSource.get(playerName, this.props.tenantId).then(
                    () => this.setState({
                        flowLibraryPlayer: playerName,
                        isCheckingFlowLibrary: false,
                    }),
                ).catch(
                    (error) => {
                        this.props.addNotification({
                            type: 'error',
                            message: error.message,
                            isPersistent: false,
                        });

                        this.setState({
                            flowLibraryPlayer: null,
                            isCheckingFlowLibrary: false,
                        });
                    },
                );
                return;
            }
            // This can be ignored because the setState is wrapped in a condition:
            // https://reactjs.org/docs/react-component.html#componentdidupdate
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                flowLibraryPlayer: null,
                isCheckingFlowLibrary: false,
            });
        }
    }

    onReportingModeChange(e) {
        this.setState({ reportingMode: e.target.value });
    }

    onCopyUrl() {
        const copy = new ClipboardEvent('copy', { dataType: 'text/plain', data: this.state.url });
        document.dispatchEvent(copy);
    }

    getUrl(player, reportingMode, id) {
        const flowId = id || this.state.id;

        let url = `${this.runtimeUri + this.props.tenantId}/play/${player}?`;
        const parameters = [`flow-id=${flowId.id}`, `flow-version-id=${flowId.versionId}`];

        if (reportingMode !== '') parameters.push(`reporting-mode=${reportingMode}`);

        url += parameters.join('&');

        return url;
    }

    getPlayer() {
        // Prefer the user selection over any other Player
        if (this.state.hasUserSelectedPlayer) {
            return this.props.selectedPlayer;
        }

        // Without user selection, we offer the Flow Library Player
        return this.state.flowLibraryPlayer ?
            this.state.flowLibraryPlayer :
            this.props.selectedPlayer;
    }

    build = () => {
        this.setState({ isBuilding: true, error: null });

        $.when(
            $.ajax({
                type: 'POST',
                url: `/api/draw/1/flow/snap/${this.props.flowId}`,
                beforeSend: (xhr) => {
                    xhr.setRequestHeader('Authorization', this.props.authenticationToken);
                    xhr.setRequestHeader('ManyWhoTenant', this.props.tenantId);
                },
            }),
            $.ajax({
                type: 'GET',
                url: `/${this.props.tenantId}/play`,
                beforeSend: (xhr) => {
                    xhr.setRequestHeader('Authorization', this.props.authenticationToken);
                    xhr.setRequestHeader('ManyWhoTenant', this.props.tenantId);
                },
            }),
        )
            .fail((response) => {
                if (response) this.setState({ error: response.responseText });
            })
            .done((snap, players) => {
                this.setState({
                    isBuilding: false,
                    id: snap[0].id,
                    url: this.getUrl(this.getPlayer(), this.state.reportingMode, snap[0].id),
                    players: players[0],
                });
            })
            .done(() => {
                findDOMNode(this.refs.url).select();
            });
    }

    runFlow(e) {
        if (!this.state.isBuilding && !this.state.isCheckingFlowLibrary) {
            let runMode = 'none';
            if (e.keyCode === 13) {
                // Enter opens the Flow
                runMode = '';
            } if (e.keyCode === 68 && e.ctrlKey) {
                // Ctrl+D opens the Flow with Debug on
                runMode = '&mode=DEBUG';
            } if (e.keyCode === 83 && e.ctrlKey) {
                // Ctrl+S opens the Flow with Debug Stepthrough on
                runMode = '&mode=DEBUG_STEPTHROUGH';
            }
            if (runMode !== 'none') {
                if (window) {
                    window.open(`${this.state.url + runMode}`, '_blank');
                }
                e.preventDefault();
            }
        }
        return false;
    }

    render() {
        let body = null;
        let url = null;

        const players = this.state.players.map(player => <option key={player} value={player}>{player}</option>);

        if (this.state.error) {
            body = (
                <Modal.Body key="error" className="text-center">
                    <h3>Build Failed</h3>
                    <p>{this.state.error}</p>
                    <button className="btn btn-primary" onClick={this.build}>Retry</button>
                </Modal.Body>
            );
        } else if (this.state.isBuilding || this.state.isCheckingFlowLibrary) {
            body = <Modal.Body key="building">{React.createElement(manywho.component.getByName('wait'), { isVisible: true }, null)}</Modal.Body>;
        } else {
            url = this.getUrl(this.getPlayer(), this.state.reportingMode);
            body = (
                <Modal.Body key="run">
                    <div role="presentation" onKeyDown={this.runFlow}>
                        <FormGroup className="group-class">
                            <ControlLabel className="label-class">Player</ControlLabel>
                            <FormControl
                                componentClass="select"
                                id="run-select-player"
                                value={this.getPlayer()}
                                onChange={(e) => {
                                    this.props.setSelectedPlayer(e);
                                    this.setState({ hasUserSelectedPlayer: true });
                                }}
                            >
                                {players}
                            </FormControl>
                        </FormGroup>
                        <FormGroup className="group-class">
                            <ControlLabel className="label-class">Reporting Mode</ControlLabel>
                            <FormControl
                                componentClass="select"
                                id="run-select-reporting"
                                value={this.state.reportingMode}
                                onChange={this.onReportingModeChange}
                            >
                                <option value="">None</option>
                                <option value="PATH">Path</option>
                                <option value="PATH_AND_VALUES">Path & Values</option>
                            </FormControl>
                        </FormGroup>
                        <FormGroup>
                            <ControlLabel>Url</ControlLabel>
                            <FormControl
                                type="text"
                                ref="url"
                                value={url}
                                onChange={this.onNameChange}
                                onBlur={this.onSaveName}
                            />
                        </FormGroup>
                    </div>
                </Modal.Body>
            );
        }

        return (
            <Modal show onHide={this.props.close} container={this.props.container}>
                <Modal.Header closeButton>
                    <Modal.Title>Run</Modal.Title>
                </Modal.Header>
                {body}
                <Modal.Footer>
                    <a
                        className="btn btn-success"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={this.state.isBuilding || this.state.isCheckingFlowLibrary}
                        id="run-flow"
                    >
                        {'Run'}
                    </a>
                    <a
                        className="btn btn-primary"
                        href={`${url}&mode=DEBUG`}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={this.state.isBuilding || this.state.isCheckingFlowLibrary}
                    >
                        {'Debug'}
                    </a>
                    <a
                        className="btn btn-primary"
                        href={`${url}&mode=DEBUG_STEPTHROUGH`}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={this.state.isBuilding || this.state.isCheckingFlowLibrary}
                    >
                        {'Debug Step-Through'}
                    </a>
                </Modal.Footer>
            </Modal>
        );
    }
}

const mapStateToProps = ({ flowLibrary: { items } }) => ({
    items,
});

const mapDispatchToProps = {
    fetchFlowLibraryItems,
    addNotification,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Run);
